import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMapPin, FiArrowUpRight, FiGlobe, FiInfo } = FiIcons;

const MemorialVisitorInsights = ({ memorial }) => {
  const locations = memorial?.visitorLocations ?? [];
  const rankedLocations = [...locations].sort(
    (a, b) => (Number(b?.count) || 0) - (Number(a?.count) || 0)
  );
  const topCount = Math.max(...rankedLocations.map(location => Number(location?.count) || 0), 0);
  const totalMappedVisits = rankedLocations.reduce(
    (total, location) => total + (Number(location?.count) || 0),
    0
  );

  return (
    <section className="bg-white p-6 md:p-8 rounded-[32px] shadow-sm border border-brand-dark">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-7">
        <div>
          <p className="text-[9px] font-black uppercase tracking-[0.2em] text-accent-gold mb-2">
            Memorial analytics
          </p>
          <h2 className="text-2xl font-serif text-accent-navy">Visitor Insights</h2>
          <p className="text-xs text-text-muted mt-1">
            A location overview of visitors honoring {memorial?.name ?? 'this legacy'}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 self-start sm:self-auto px-3 py-2 rounded-xl bg-brand-light text-accent-navy text-[9px] font-black uppercase tracking-widest">
          <SafeIcon icon={FiGlobe} className="w-3.5 h-3.5 text-accent-gold" />
          Location overview
        </div>
      </div>

      {rankedLocations.length > 0 ? (
        <div className="space-y-4">
          {rankedLocations.map((location, index) => {
            const count = Number(location?.count) || 0;
            const share = topCount ? Math.round((count / topCount) * 100) : 0;

            return (
              <motion.div
                key={`${location?.name ?? 'location'}-${index}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                className="rounded-2xl border border-brand-dark bg-white p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-light text-accent-gold flex items-center justify-center shrink-0">
                    <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-accent-navy truncate">
                          {location?.name ?? 'Unspecified location'}
                        </h3>
                        <p className="text-[9px] text-text-muted uppercase tracking-widest mt-1">
                          {location?.type ?? 'Visitor location'}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-serif text-accent-navy">{count.toLocaleString()}</p>
                        <p className="text-[8px] font-black uppercase tracking-widest text-text-muted">
                          mapped visits
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 h-1.5 rounded-full bg-brand-light overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${share}%` }}
                        transition={{ duration: 0.7, delay: index * 0.08 }}
                        className="h-full rounded-full bg-accent-gold"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-brand-dark py-10 text-center">
          <SafeIcon icon={FiMapPin} className="w-6 h-6 mx-auto mb-3 text-text-muted" />
          <p className="text-sm font-serif text-accent-navy">No visitor locations yet</p>
          <p className="text-xs text-text-muted mt-1">
            Location insights will appear when location data is available.
          </p>
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-brand-light pt-5">
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <SafeIcon icon={FiArrowUpRight} className="w-4 h-4 text-accent-gold" />
          <span>
            {totalMappedVisits.toLocaleString()} mapped visits across {rankedLocations.length} locations
          </span>
        </div>
        <div className="flex items-start gap-2 text-[10px] leading-relaxed text-text-muted sm:max-w-[55%]">
          <SafeIcon icon={FiInfo} className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Location counts use the memorial&apos;s current sample data; live visit tracking is not connected.
          </span>
        </div>
      </div>
    </section>
  );
};

export default MemorialVisitorInsights;